/**
 * error-recovery.tsx — Error display and recovery components.
 *
 * planTEK External Portal — graceful error states with retry.
 * Errors are logged to console but NOT exposed to users.
 */
import { Component, type ReactNode } from 'react';
import { AlertCircle, Clock } from 'lucide-react';

// ---------------------------------------------------------------------------
// PanelError — error card with optional retry
// ---------------------------------------------------------------------------
export interface PanelErrorProps {
  onRetry?: () => void;
  message?: string;
}

export function PanelError({
  onRetry,
  message = 'Something went wrong',
}: PanelErrorProps) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex flex-col gap-3 py-4"
    >
      <div className="flex items-start gap-2.5">
        <AlertCircle
          className="w-5 h-5 shrink-0 mt-0.5"
          style={{ color: 'var(--destructive, #dc2626)' }}
        />
        <p
          className="text-sm font-medium"
          style={{ color: 'var(--foreground)' }}
        >
          {message}
        </p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="self-start rounded-lg border px-3 text-sm font-medium transition-colors min-h-[44px]"
          style={{
            borderColor: 'var(--border, #e5e7eb)',
            color: 'var(--foreground)',
            backgroundColor: 'transparent',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PanelTimeout — network timeout with retry
// ---------------------------------------------------------------------------
export interface PanelTimeoutProps {
  onRetry?: () => void;
}

export function PanelTimeout({ onRetry }: PanelTimeoutProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-3 py-4"
    >
      <div className="flex items-start gap-2.5">
        <Clock
          className="w-5 h-5 shrink-0 mt-0.5"
          style={{ color: '#d97706' }}
        />
        <p
          className="text-sm"
          style={{ color: 'var(--muted-foreground, #6b7280)' }}
        >
          Taking longer than expected — retrying...
        </p>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="self-start rounded-lg border px-3 text-sm font-medium transition-colors min-h-[44px]"
          style={{
            borderColor: 'var(--border, #e5e7eb)',
            color: 'var(--foreground)',
            backgroundColor: 'transparent',
          }}
        >
          Try again
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PanelErrorBoundaryWithRetry — class component error boundary with retry
// ---------------------------------------------------------------------------
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface PanelErrorBoundaryWithRetryProps {
  children: ReactNode;
  panelTitle: string;
  fallback?: ReactNode;
}

export class PanelErrorBoundaryWithRetry extends Component<
  PanelErrorBoundaryWithRetryProps,
  ErrorBoundaryState
> {
  constructor(props: PanelErrorBoundaryWithRetryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[PanelError]', this.props.panelTitle, error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <PanelError onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}
